import {BrowserModule} from '@angular/platform-browser';
import {NgModule, Provider} from '@angular/core';
import {HttpClientModule, HTTP_INTERCEPTORS} from '@angular/common/http';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {UIRouterModule, UIView} from '@uirouter/angular';
import {NgOptimizedImage} from "@angular/common";
import { SvgIconComponent, provideAngularSvgIcon } from 'angular-svg-icon';

import {AppComponent} from './app/app.component';
import {LoginComponent} from './login/login.component';
import {HomeComponent} from './home/home.component';
import {APP_STATES} from './app.states';
import {routerConfigFn} from './router.config';
import {AbstractComponent} from './abstract/abstract.component';
import {AuthenticationService, AuthInterceptorService} from "./core";
import {AppAlertComponent} from './app-alert/app-alert.component';
import {OldAlertComponent} from './app-alert/old-alert/old-alert.component';
import {LoaderComponent} from './common/loader/loader.component';
import { TopMenuComponent } from './top-menu/top-menu.component';
import { AdminComponent } from './admin/admin.component';
import { SettingsComponent } from './settings/settings/settings.component';
import { SettingsAccountComponent } from './settings/settings-account/settings-account.component';
import { PolicyPlComponent } from './app-alert/policy/policy-pl/policy-pl.component';
import { PolicyEnComponent } from './app-alert/policy/policy-en/policy-en.component';
import { PolicyDeComponent } from './app-alert/policy/policy-de/policy-de.component';
import { RegulationsPlComponent } from './app-alert/regulations/regulations-pl/regulations-pl.component';
import { RegulationsDeComponent } from './app-alert/regulations/regulations-de/regulations-de.component';
import { RegulationsEnComponent } from './app-alert/regulations/regulations-en/regulations-en.component';
import { RegulationsKoltonPlComponent } from './app-alert/regulations/regulations-kolton-pl/regulations-kolton-pl.component';
import { RegulationsKoltonEnComponent } from './app-alert/regulations/regulations-kolton-en/regulations-kolton-en.component';
import { RegulationsSerwis24Component } from './app-alert/regulations/regulations-serwis24/regulations-serwis24.component';
import { GeneralPolicyPlComponent } from './app-alert/policy/general-policy-pl/general-policy-pl.component';
import { GeneralPolicyEnComponent } from './app-alert/policy/general-policy-en/general-policy-en.component';
import { PolicyKoltonPlComponent } from './app-alert/policy/policy-kolton-pl/policy-kolton-pl.component';
import { PolicyKoltonEnComponent } from './app-alert/policy/policy-kolton-en/policy-kolton-en.component';
import { DeleteAccountComponent } from './help/delete-account/delete-account.component';
import { RegistrationNewModuleComponent } from './settings/registration-new-module/registration-new-module.component';
import { ProgressBarComponent } from './app-alert/progress-bar/progress-bar.component';
import { RemoteLoginComponent } from './remote-login/remote-login.component';
import { SettingsNotificationsComponent } from './settings/settings-notifications/settings-notifications.component';
import { SettingsManualsComponent } from './settings/settings-manuals/settings-manuals.component';
import { AdditionalSoftwareComponent } from './settings/additional-software/additional-software.component';
import { ControllerSoftwareComponent } from './settings/controller-software/controller-software.component';
import { SettingsTilesComponent } from './settings/settings-tiles/settings-tiles.component';
import {
    DoublePipe, FormatPipe, LongDayNamePipe, IntervalHourPipe, IntervalMinPipe,
    MonthNamePipe, OrderByConnectionKeyPipe,
    WeekDayPipe, OrderByObjectPipe, ShortDayNamePipe, QuarterPipe, TilesOrderPipe,
    UnderfloorModePipe, UnderfloorStatePipe, UnderfloorSensorTypePipe,
    ZipCodePipe
} from './shared';
import {SubmenuComponent} from './submenu/submenu.component';
import { LoginHeaderComponent } from './login-header/login-header.component';
import { FooterComponent } from './footer/footer.component';
import { ZonesComponent } from './zones/zones/zones.component';
import { InactiveModuleComponent } from './inactive-module/inactive-module.component';
import { IconComponent } from './icon/icon.component';
import { ZonesContainerComponent } from './zones/zones-container/zones-container.component';
import { FloatingSubtitleComponent } from './common/floating-subtitle/floating-subtitle.component';
import { ZonesContainerTileComponent } from './zones/zones-container-tile/zones-container-tile.component';
import { ConnectionTableComponent } from './zones/connection-table/connection-table.component';
import { RegistrationComponent } from './registration/registration.component';
import { RecaptchaModule } from "ng-recaptcha";
import { ZonesTileComponent } from './zones/zones-tile/zones-tile.component';
import { AdditionalContactComponent } from './zones/additional-contact/additional-contact.component';
import { BuiltInValveComponent } from './zones/built-in-valve/built-in-valve.component';
import { TemperatureSensorComponent } from './zones/temperature-sensor/temperature-sensor.component';
import { RelayComponent } from './zones/relay/relay.component';
import { DHWOnOffComponent } from './zones/dhw-on-off/dhw-on-off.component';
import { DhwWithEventsComponent } from './zones/dhw-with-events/dhw-with-events.component';
import { ZonesAlarmTileComponent } from './zones/zones-alarm-tile/zones-alarm-tile.component';
import { DhwForZonesComponent } from './zones/dhw-for-zones/dhw-for-zones.component';
import { OpenThermComponent } from './zones/opentherm/opentherm.component';
import { ZoneComponent } from './zones/zone/zone.component';
import { ConstantTempComponent } from './zones/constant-temp/constant-temp.component';
import { TimeLimitationComponent } from './zones/time-limitation/time-limitation.component';
import { ZoneInfoComponent } from './zones/zone-info/zone-info.component';
import { ZoneContainerInfoComponent } from './zones/zone-container-info/zone-container-info.component';
import { ValveComponent } from './zones/valve/valve.component';
import { ValveInfoComponent } from './zones/valve-info/valve-info.component';
import { AdditionalContactCardComponent } from './zones/additional-contact-card/additional-contact-card.component';
import { AdditionalContactInfoComponent } from './zones/additional-contact-info/additional-contact-info.component';
import { OpenthermCardComponent } from './zones/opentherm-card/opentherm-card.component';
import { OpenthermCardInfoComponent } from './zones/opentherm-card-info/opentherm-card-info.component';
import { SimpleValveCardComponent } from "./zones/simple-valve/simple-valve-card/simple-valve-card.component";
import { SimpleValveInfoComponent } from "./zones/simple-valve/simple-valve-info/simple-valve-info.component";
import { SimpleValveParametersComponent } from './zones/simple-valve/simple-valve-parameters/simple-valve-parameters.component';
import { SimpleValveComponent } from './zones/simple-valve/simple-valve.component'
import { AccountActivationComponent } from './account-activation/account-activation.component';
import { ZoneScheduleComponent } from './zones/zone-schedule/zone-schedule.component';
import { ZoneScheduleSettingsComponent } from './zones/zone-schedule-settings/zone-schedule-settings.component';
import { ZoneScheduleSelectComponent } from './zones/zone-schedule-select/zone-schedule-select.component';
import { ConfirmDeleteAccountComponent } from './confirm-delete-account/confirm-delete-account.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { TilesComponent } from './tiles/tiles/tiles.component';
import { TileComponent } from './tiles/tile/tile.component';
import { TileErrorComponent } from './tiles/tiles-components/tile-error/tile-error.component';
import { TileAlarmComponent } from './tiles/tiles-components/tile-alarm/tile-alarm.component';
import { TileTemperatureSensorComponent } from './tiles/tiles-components/tile-temperature-sensor/tile-temperature-sensor.component';
import { TileRelayComponent } from './tiles/tiles-components/tile-relay/tile-relay.component';
import { TileFireSensorComponent } from './tiles/tiles-components/tile-fire-sensor/tile-fire-sensor.component';
import { TileTextInformationComponent } from './tiles/tiles-components/tile-text-information/tile-text-information.component';
import { TileUniversalStatusComponent } from './tiles/tiles-components/tile-universal-status/tile-universal-status.component';
import { TileBuiltInValveComponent } from './tiles/tiles-components/tile-built-in-valve/tile-built-in-valve.component';
import { TileFanComponent } from './tiles/tiles-components/tile-fan/tile-fan.component';
import { CircleProgressComponent } from './common/circle-progress/circle-progress.component';
import { TileFuelSupplyComponent } from './tiles/tiles-components/tile-fuel-supply/tile-fuel-supply.component';
import { TileControllerSoftwareComponent } from './tiles/tiles-components/tile-controller-software/tile-controller-software.component';
import { CircleProgressThinComponent } from './common/circle-progress-thin/circle-progress-thin.component';
import { TileDateComponent } from './tiles/tiles-components/tile-date/tile-date.component';
import { TileDisinfectionComponent } from './tiles/tiles-components/tile-disinfection/tile-disinfection.component';
import { TileAdditionalValveComponent } from './tiles/tiles-components/tile-additional-valve/tile-additional-valve.component';
import { IndicatedValueComponent } from './common/indicated-value/indicated-value.component';
import { TileAdditionalPumpComponent } from './tiles/tiles-components/tile-additional-pump/tile-additional-pump.component';
import { TilePeripheralSystemSoftwareComponent } from './tiles/tiles-components/tile-peripheral-system-software/tile-peripheral-system-software.component';
import { TileFiringUpComponent } from './tiles/tiles-components/tile-firing-up/tile-firing-up.component';
import { SignalStrengthComponent } from './common/signal-strength/signal-strength.component';
import { TileOpenthermComponent } from './tiles/tiles-components/tile-opentherm/tile-opentherm.component';
import { PolicyComponent } from './policy/policy.component';
import { MaintenanceAlertComponent } from './maintenance-alert/maintenance-alert.component';
import { UserInformationComponent } from './popups/user-information/user-information.component';
import { SimpleMessageComponent } from './popups/simple-message/simple-message.component';
import { PopupPolicyComponent } from "./popups/policy/policy.component";
import { ControlsComponent } from './controls/controls/controls.component';
import { CheckboxComponent} from "./controls/control-components/checkbox/checkbox.component";
import { OneSwitchComponent } from './controls/control-components/one-switch/one-switch.component';
import { ChangeAccountEmailController } from './change-account-email/change-account-email-controller.component';
import { ScheduleMultiComponent } from './controls/control-components/schedule-multi/schedule-multi.component';
import {NgxSliderModule} from "@angular-slider/ngx-slider";
import { AlarmHistoryComponent } from "./alarm-history/alarm-history.component";
import { DatePickerComponent } from './date-picker/date-picker.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { YesNoComponent } from './controls/control-components/yes-no/yes-no.component';
import { OnOffComponent } from './controls/control-components/on-off/on-off.component';
import { ManufacturerDataComponent } from './controls/control-components/manufacturer-data/manufacturer-data.component';
import { CheckboxExpandedComponent } from './controls/control-components/checkbox-expanded/checkbox-expanded.component';
import { TwoSwitchComponent } from './controls/control-components/two-switch/two-switch.component';
import { SelectBoxComponent } from './controls/control-components/select-box/select-box.component';
import { HeatingCurveComponent } from './controls/control-components/heating-curve/heating-curve.component';
import { ScheduleOnOffComponent } from './controls/control-components/schedule-on-off/schedule-on-off.component';
import { InputControlComponent } from './controls/control-components/input-control/input-control.component';
import { TimeModeComponent } from './controls/control-components/time-mode/time-mode.component';
import { HeatingCurveChartComponent } from './controls/control-components/heating-curve-chart/heating-curve-chart.component';
import { HeatingCurveShiftComponent } from './controls/control-components/heating-curve-shift/heating-curve-shift.component';
import { GroupCodeComponent } from './controls/control-components/group-code/group-code.component';
import { DataTableComponent } from './controls/control-components/data-table/data-table.component';
import { DataTableDifferentComponent } from './controls/control-components/data-table-different/data-table-different.component';
import { DateFormatControlComponent } from './controls/control-components/date-format-control/date-format-control.component';
import { DatePickerControlComponent } from './controls/control-components/date-picker-control/date-picker-control.component';
import { DateIntervalComponent } from './controls/control-components/date-interval/date-interval.component';
import { ScheduleIntervalsComponent } from './controls/control-components/schedule-intervals/schedule-intervals.component';
import { SchedulePlusMinusComponent } from './controls/control-components/schedule-plus-minus/schedule-plus-minus.component';
import { OrderByIdPipe } from './shared';
import { StatisticsComponent } from './statistics/statistics.component';
import { NotFoundComponent } from './not-found/not-found.component';
import {ZoneIconComponent} from "./icons/zone-icon/zone-icon.component";
import {SubtitlePipe} from "./shared/subtitle.pipe";
import {CardComponent} from "./common/card/card.component";
import {
    SettingsMenuComponent
} from "./settings/settings-menu/settings-menu.component";
import {
    FormLabelComponent
} from "./common/forms/form-label/form-label.component";
import {FormItemComponent} from "./common/forms/form-item/form-item.component";
import {
    FormSectionComponent
} from "./common/forms/form-section/form-section.component";
import {FormTextComponent} from "./common/forms/form-text/form-text.component";
import {
    FormEmailInputComponent
} from "./common/forms/form-email-input/form-email-input.component";
import {
    FormButtonComponent
} from "./common/forms/form-button/form-button.component";
import {
    FormPasswordInputComponent
} from "./common/forms/form-password-input/form-password-input.component";
import {
    FormTextInputComponent
} from "./common/forms/form-text-input/form-text-input.component";
import {AppLinkComponent} from "./common/app-link/app-link.component";
import {
    FormCardButtonComponent
} from "./common/forms/form-card-button/form-card-button.component";
import {AppDialogComponent} from "./common/app-dialog/app-dialog.component";
import {
    EmailFormComponent
} from "./settings/settings-account/email-form/email-form.component";
import {
    PasswordFormComponent
} from "./settings/settings-account/password-form/password-form.component";
import {
    DeleteAccountFormComponent
} from "./settings/settings-account/delete-account-form/delete-account-form.component";
import {
    AdditionalInfoFormComponent
} from "./settings/settings-account/additional-info-form/additional-info-form.component";
import {
    FormCheckboxComponent
} from "./common/forms/form-checkbox/form-checkbox.component";
import {
    Service24FormComponent
} from "./settings/settings-account/service24-form/service24-form.component";
import {
    ApiTokenFormComponent
} from "./settings/settings-account/api-token-form/api-token-form.component";
import {
    NotificationInfoComponent
} from "./settings/settings-notifications/notification-info/notification-info.component";
import {
    NotificationSwitchComponent
} from "./settings/settings-notifications/notification-switch/notification-switch.component";
import {
    NotificationEmailComponent
} from "./settings/settings-notifications/notification-email/notification-email.component";
import {
    NotificationTimeComponent
} from "./settings/settings-notifications/notification-time/notification-time.component";
import {
    HoldWrapperComponent
} from "./common/hold-wrapper/hold-wrapper.component";
import {
    FormItemTextComponent
} from "./common/forms/form-item-text/form-item-text.component";
import {
    FormActionButtonsComponent
} from "./common/forms/form-action-buttons/form-action-btns.component";
import {
    AlertDialogComponent
} from "./app-alert/alert-dialog/alert-dialog.component";
import {
    ConfirmDialogComponent
} from "./app-alert/confirm-dialog/confirm-dialog.component";
import {
    CountryListComponent
} from "./settings/settings-module/country-list/country-list.component";
import {
    TimeZonesListComponent
} from "./settings/settings-module/time-zones-list/time-zones-list.component";
import {FormLinkComponent} from "./common/forms/form-link/form-link.component";
import {
    FormValueButtonComponent
} from "./common/forms/form-value-button/form-value-button.component";


const authInterceptorProvider: Provider = {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptorService,
    multi: true
};

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    HomeComponent,
    AbstractComponent,
    AppAlertComponent,
    OldAlertComponent,
    TopMenuComponent,
    AdminComponent,
    ZipCodePipe,
    SettingsComponent,
    SettingsAccountComponent,
    PolicyPlComponent,
    PolicyEnComponent,
    PolicyDeComponent,
    RegulationsPlComponent,
    RegulationsDeComponent,
    RegulationsEnComponent,
    RegulationsKoltonPlComponent,
    RegulationsKoltonEnComponent,
    RegulationsSerwis24Component,
    GeneralPolicyPlComponent,
    GeneralPolicyEnComponent,
    PolicyKoltonPlComponent,
    PolicyKoltonEnComponent,
    DeleteAccountComponent,
    RegistrationNewModuleComponent,
    ProgressBarComponent,
    RemoteLoginComponent,
    SettingsNotificationsComponent,
    SettingsManualsComponent,
    AdditionalSoftwareComponent,
    ControllerSoftwareComponent,
    SettingsTilesComponent,
    DoublePipe,
    WeekDayPipe,
    MonthNamePipe,
    SubmenuComponent,
    LoginHeaderComponent,
    FooterComponent,
    ZonesComponent,
    InactiveModuleComponent,
    IconComponent,
    ZonesContainerComponent,
    FloatingSubtitleComponent,
    ZonesContainerTileComponent,
    ConnectionTableComponent,
    OrderByConnectionKeyPipe,
    FormatPipe,
    UnderfloorModePipe,
    UnderfloorStatePipe,
    UnderfloorSensorTypePipe,
    RegistrationComponent,
    ZonesTileComponent,
    AdditionalContactComponent,
    BuiltInValveComponent,
    TemperatureSensorComponent,
    RelayComponent,
    DHWOnOffComponent,
    DhwWithEventsComponent,
    ZonesAlarmTileComponent,
    DhwForZonesComponent,
    OpenThermComponent,
    ZoneComponent,
    ConstantTempComponent,
    TimeLimitationComponent,
    ZoneInfoComponent,
    ZoneContainerInfoComponent,
    ValveComponent,
    ValveInfoComponent,
    AdditionalContactCardComponent,
    AdditionalContactInfoComponent,
    OpenthermCardComponent,
    OpenthermCardInfoComponent,
    SimpleValveComponent,
    SimpleValveCardComponent,
    SimpleValveInfoComponent,
    SimpleValveParametersComponent,
    ZoneScheduleComponent,
    ZoneScheduleSettingsComponent,
    ShortDayNamePipe,
    ZoneScheduleSelectComponent,
    ConfirmDeleteAccountComponent,
    ResetPasswordComponent,
    TilesComponent,
    OrderByObjectPipe,
    TileComponent,
    TilesOrderPipe,
    TileErrorComponent,
    TileAlarmComponent,
    TileTemperatureSensorComponent,
    TileRelayComponent,
    TileFireSensorComponent,
    TileTextInformationComponent,
    TileUniversalStatusComponent,
    TileBuiltInValveComponent,
    TileFanComponent,
    CircleProgressComponent,
    TileFuelSupplyComponent,
    TileControllerSoftwareComponent,
    CircleProgressThinComponent,
    TileDateComponent,
    TileDisinfectionComponent,
    TileAdditionalValveComponent,
    IndicatedValueComponent,
    TileAdditionalPumpComponent,
    TilePeripheralSystemSoftwareComponent,
    TileFiringUpComponent,
    SignalStrengthComponent,
    TileOpenthermComponent,
    ControlsComponent,
    CheckboxComponent,
    OneSwitchComponent,
    AccountActivationComponent,
    ChangeAccountEmailController,
    ScheduleMultiComponent,
    QuarterPipe,
    PolicyComponent,
    PopupPolicyComponent,
    MaintenanceAlertComponent,
    UserInformationComponent,
    SimpleMessageComponent,
    AlarmHistoryComponent,
    DatePickerComponent,
    ForgotPasswordComponent,
    YesNoComponent,
    OnOffComponent,
    ManufacturerDataComponent,
    CheckboxExpandedComponent,
    TwoSwitchComponent,
    SelectBoxComponent,
    HeatingCurveComponent,
    ScheduleOnOffComponent,
    InputControlComponent,
    TimeModeComponent,
    HeatingCurveChartComponent,
    HeatingCurveShiftComponent,
    GroupCodeComponent,
    DataTableComponent,
    DataTableDifferentComponent,
    DateFormatControlComponent,
    DatePickerControlComponent,
    DateIntervalComponent,
    ScheduleIntervalsComponent,
    LongDayNamePipe,
    IntervalHourPipe,
    IntervalMinPipe,
    SchedulePlusMinusComponent,
    OrderByIdPipe,
    StatisticsComponent,
    NotFoundComponent,
  ],

    imports: [
        UIRouterModule.forRoot({
            states: APP_STATES,
            initial: {state: 'start'},
            config: routerConfigFn,
        }),
        BrowserModule,
        FormsModule,
        HttpClientModule,
        NgOptimizedImage,
        LoaderComponent,
        SvgIconComponent,
        RecaptchaModule,
        NgxSliderModule,
        ReactiveFormsModule,
        ZoneIconComponent,
        ReactiveFormsModule,
        SubtitlePipe,
        CardComponent,
        SettingsMenuComponent,
        UIRouterModule,
        FormLabelComponent,
        FormItemComponent,
        FormSectionComponent,
        FormTextComponent,
        FormEmailInputComponent,
        FormButtonComponent,
        FormPasswordInputComponent,
        FormTextInputComponent,
        AppLinkComponent,
        FormCardButtonComponent,
        AppDialogComponent,
        EmailFormComponent,
        PasswordFormComponent,
        DeleteAccountFormComponent,
        AdditionalInfoFormComponent,
        FormCheckboxComponent,
        Service24FormComponent,
        ApiTokenFormComponent,
        NotificationInfoComponent,
        NotificationSwitchComponent,
        NotificationEmailComponent,
        NotificationTimeComponent,
        HoldWrapperComponent,
        FormItemTextComponent,
        FormActionButtonsComponent,
        AlertDialogComponent,
        ConfirmDialogComponent,
        CountryListComponent,
        TimeZonesListComponent,
        FormLinkComponent,
        FormValueButtonComponent,
    ],
    bootstrap: [UIView],
    exports: [
        IconComponent,
    ],
    providers: [
        AuthenticationService,
        authInterceptorProvider,
        DoublePipe,
        FormatPipe,
        IntervalHourPipe,
        MonthNamePipe,
        UnderfloorModePipe,
        UnderfloorSensorTypePipe,
        UnderfloorStatePipe,
        WeekDayPipe,
        ZipCodePipe,
        provideAngularSvgIcon(),
    ]
})
export class AppModule {
}
